/**
 * Audit Configuration Interface
 * 
 * Defines the contract for entity-specific audit configuration
 * to be passed to generic audit components.
 */

/**
 * Section definition with path matching patterns
 */
export interface SectionDefinition {
  value: string // Section identifier (e.g., "Core Information")
  label: string // Display label
  pathPatterns: string[] // Patterns to match (e.g., ["CoreInfo/*", "ClientInfo/*"])
}

/**
 * Configuration for mapping audit data to UI display
 */
export interface AuditFieldConfig {
  /**
   * Available sections with path matching rules (ordered, first match wins)
   * Used for both mapping propertyPath to sections and for filter options
   */
  availableSections: SectionDefinition[]

  /**
   * Field label mappings (PascalCase fieldName → display label)
   * e.g., { "BillingTypeCode": "Billing Type", "StartDate": "Start Date" }
   */
  fieldLabels: Record<string, string>

  /**
   * Path segment formatter
   * Formats individual segments of a propertyPath for display
   * e.g., "Milestones" → "Milestone", "0" (index) → "#1"
   * Can return undefined for organizational containers (CoreInfo, etc.) that shouldn't appear as parent labels
   * Used to build heading labels like "Milestone #1" or "Revenue Recognition / Period #1"
   */
  formatPathSegment?: (segment: string, isIndex: boolean) => string | undefined

  /**
   * Value formatter function
   * Converts raw values to user-friendly display strings
   * 
   * @param value - The raw value from the audit API
   * @param propertyPath - The property path (e.g., "CoreInfo/BillingTypeCode")
   * @param fieldName - The field name (e.g., "BillingTypeCode")
   * @returns Formatted string for display
   */
  formatValue: (value: any, propertyPath: string, fieldName: string) => string

  /**
   * Status formatter function
   * Converts status codes to user-friendly labels
   */
  formatStatus: (statusCode: string) => string

  /**
   * Action formatter function
   * Converts action codes to user-friendly labels
   */
  formatAction: (actionCode: string) => string

  /**
   * Fields left out of the history, as paths with their array indices removed
   * (e.g. "Workers/ScheduledBudgetedHours/Id"). For values that identify a record to the system but
   * mean nothing to a reader.
   */
  hiddenFields?: string[]
}

// TODO: Why are these functions here? We should move them to the audit-utils.ts file

/**
 * Maps a propertyPath to a section using availableSections
 * Checks exact matches first, then wildcards (regardless of section order)
 * 
 * @param propertyPath - e.g., "CoreInfo/BillingTypeCode", "Workers/0/FirstDayOfWork"
 * @param availableSections - Array of section definitions with patterns
 * @returns Section name or "System" if no match
 */
export function mapPathToSection(
  propertyPath: string,
  availableSections: SectionDefinition[]
): string {
  // First pass: check exact matches only (no wildcards)
  for (const section of availableSections) {
    for (const pattern of section.pathPatterns) {
      if (!pattern.includes("*") && matchesPattern(propertyPath, pattern)) {
        return section.value
      }
    }
  }
  
  // Second pass: check wildcard matches
  for (const section of availableSections) {
    for (const pattern of section.pathPatterns) {
      if (pattern.includes("*") && matchesPattern(propertyPath, pattern)) {
        return section.value
      }
    }
  }
  
  return "System"
}

/**
 * Checks if a propertyPath matches a pattern
 * 
 * Pattern types:
 * - Exact: "CoreInfo/Name" matches "CoreInfo/Name" only
 * - Wildcard: "Workers/*" matches "Workers/0", "Workers/1/FirstDayOfWork", etc.
 * - Catch-all: "*" matches anything
 * 
 * @param path - The actual propertyPath from audit data
 * @param pattern - The pattern to match against
 * @returns true if matches, false otherwise
 */
function matchesPattern(path: string, pattern: string): boolean {
  // Catch-all pattern
  if (pattern === "*") {
    return true
  }

  // Exact match (no wildcard)
  if (!pattern.includes("*")) {
    return path === pattern
  }

  // Wildcard pattern (e.g., "Workers/*")
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -2) // Remove "/*"
    return path === prefix || path.startsWith(prefix + "/")
  }

  return false
}

